<template>
    <div class="relative bg-white shadow-md rounded-xl p-4 m-4">
        <MonkeyLoader v-if="isLoading" />

        <h2 class="text-xl font-semibold mb-6 text-gray-800 border-b pb-2">Goods Receipt Update</h2>
        <form @submit.prevent="saveGoodsReceipt">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="poNumber">Pilih GR Pending</label>
                    <v-select id="poNumber" v-model="form.grDetailId" :options="pendingGrOptions"
                        :reduce="gr => gr.grDetailId" label="label" placeholder="-- Ketik atau Pilih GR Pending --"
                        @option:selected="handleGrSelection" class="input-border bg-white">
                    </v-select>
                </div>
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="customerName">Customer Name</label>
                    <input id="customerName" type="text" :value="selectedPoData.customerName"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="poType">PO Type</label>
                    <input id="poType" type="text" :value="selectedPoData.poType"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
            </div>

            <h3 class="text-lg font-semibold mb-2 text-gray-700">Detail Item</h3>
            <div class="overflow-x-auto border rounded-lg">
                <table class="min-w-full bg-white">
                    <thead class="bg-blue-100">
                        <tr>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Part Name</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Desc</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Qty PO</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Unit Price</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase">Total Amount</th>
                            <th class="py-3 px-4 text-left text-xs font-bold text-blue-800 uppercase w-32">Term</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="!form.grDetailId">
                            <td colspan="6" class="text-center py-4 text-gray-500">Pilih GR pending untuk menampilkan
                                item.
                            </td>
                        </tr>
                        <tr v-else-if="form.items.length === 0">
                            <td colspan="6" class="text-center py-4 text-gray-500">Loading item atau tidak ada item...
                            </td>
                        </tr>
                        <tr v-for="(item, index) in form.items" :key="item.productId"
                            class="odd:bg-white even:bg-blue-50">
                            <td class="py-2 px-4">{{ item.partName }}</td>
                            <td class="py-2 px-4">{{ item.partDesc }}</td>
                            <td class="py-2 px-4">{{ item.qty }}</td>
                            <td class="py-2 px-4 text-right">{{ formatCurrency(item.unitPrice) }}</td>
                            <td class="py-2 px-4 text-right">{{ formatCurrency(item.qty * item.unitPrice) }}</td>
                            <td v-if="index === 0" class="py-2 px-4 align-top text-center" :rowspan="form.items.length">
                                {{ selectedPoData.paymentTerm }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex items-end gap-4 mt-4">
                <div class="w-1/4">
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="grPercentageAmount">Persentase Amount
                        GR (%)</label>
                    <input id="grPercentageAmount" type="number" v-model.number="form.grPercentageAmount"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 my-6 items-end">
                <div>
                    <label class="block text-gray-700 text-sm font-bold mb-2" for="submitGrDate">Submit GR Date</label>
                    <input id="submitGrDate" type="date" v-model="form.submitGrDate"
                        class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                        disabled>
                </div>
                <div class="flex items-end gap-2">
                    <div class="flex-grow">
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="receivedDate">Received
                            Date</label>
                        <input id="receivedDate" type="date" v-model="form.receivedDate"
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">
                    </div>
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="aging">Aging</label>
                        <input id="aging" type="text" :value="agingDays"
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 bg-gray-200"
                            style="width: 100px;" disabled>
                    </div>
                </div>

                <div class="md:col-span-2 bg-gray-50 p-3 rounded-lg border">
                    <div class="flex items-center mb-2">
                        <input id="isFinanceReceivedCheck" type="checkbox" v-model="form.isFinanceReceived"
                            class="h-4 w-4 mr-2">
                        <label class="text-gray-700 text-sm font-bold" for="isFinanceReceivedCheck">Received by
                            Finance</label>
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-gray-600 text-xs font-bold mb-1" for="financeReceivedDate">Finance
                                Date</label>
                            <input id="financeReceivedDate" type="date" v-model="form.financeReceivedDate"
                                :disabled="!form.isFinanceReceived" :class="{ 'bg-gray-200': !form.isFinanceReceived }"
                                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">
                        </div>
                        <div>
                            <label class="block text-gray-600 text-xs font-bold mb-1" for="financeUser">Finance
                                User</label>
                            <select id="financeUser" v-model="form.financeUserId" :disabled="!form.isFinanceReceived"
                                :class="{ 'bg-gray-200': !form.isFinanceReceived }"
                                class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700">
                                <option :value="null">-- Pilih User --</option>
                                <option v-for="user in financeUserOptions" :key="user.id" :value="user.id">{{ user.name
                                }}</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div class="md:col-span-2 grid grid-cols-1 gap-4">
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="historyRemarks">History
                            Remark</label>
                        <textarea id="historyRemarks" v-model="historyRemark" rows="4"
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-600 bg-gray-100 text-xs"
                            disabled></textarea>
                    </div>
                    <div>
                        <label class="block text-gray-700 text-sm font-bold mb-2" for="remarks">New Remark /
                            Comment</label>
                        <textarea id="remarks" v-model="form.remarks" rows="3"
                            placeholder="Tambahkan komentar baru di sini..."
                            class="input-border shadow appearance-none border rounded w-full py-2 px-3 text-gray-700"></textarea>
                    </div>
                </div>
                <div class="summary-box">
                    <h4 class="font-bold text-lg mb-3">Ringkasan GR</h4>
                    <div class="flex justify-between items-center text-blue-700 mb-2">
                        <span class="font-bold">Total PO Amount:</span>
                        <span class="font-semibold">{{ formatCurrency(totalPoAmount) }}</span>
                    </div>
                    <div class="flex justify-between items-center text-blue-700 mb-2">
                        <span class="font-bold">Amount GR (Saat Ini):</span>
                        <span class="font-bold text-lg">{{ calculatedGrAmountFormatted }}</span>
                    </div>
                    <hr class="my-2 border-t border-blue-200">
                    <div class="flex justify-between items-center text-red-700 mb-2">
                        <span class="font-bold">Balance GR:</span>
                        <span class="font-semibold">{{ newBalanceGrFormatted }}</span>
                    </div>
                </div>
            </div>

            <div class="flex justify-center mt-8 pt-4 border-t gap-4">
                <button type="submit" class="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-8 rounded-lg">
                    Update
                </button>
                <button type="button" v-if="form.grDetailId && !form.isFinanceReceived" @click="voidGoodsReceipt"
                    class="bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-8 rounded-lg">
                    Void GR
                </button>
                <button type="button" @click="resetForm"
                    class="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-8 rounded-lg">
                    Cancel
                </button>
            </div>
        </form>
    </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import vSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';
import MonkeyLoader from '../Layout/MonkeyLoader.vue';

const API_ENDPOINTS = {
    pendingGrList: `${window.location.origin}/api/goodsreceipts`,
    grDetailById: (grDetailId) => `${window.location.origin}/api/grdetails/${grDetailId}`,
    updateGoodsReceiptDetails: (grDetailId) => `${window.location.origin}/api/goodsreceipts/details/${grDetailId}`,
    voidGoodsReceipt: (grDetailId) => `${window.location.origin}/api/goodsreceipts/details/${grDetailId}`,
    financeUsers: 'https://meeting-backend.iotech.my.id/ext-users'
};

// Helper untuk mendapatkan tanggal hari ini dalam format YYYY-MM-DD
const getTodayDate = () => new Date().toISOString().split('T')[0];

const getInitialFormData = () => ({
    grId: null,
    grDetailId: null,
    poId: null,
    submitGrDate: '',
    remarks: '',
    items: [],
    grPercentageAmount: null,
    // DIUBAH: Default value diatur ke hari ini
    receivedDate: getTodayDate(),
    isFinanceReceived: false,
    financeReceivedDate: getTodayDate(),
    financeUserId: null,
});

export default {
    name: 'GoodsReceiptUpdate',
    components: {
        'v-select': vSelect,
        MonkeyLoader,
    },
    data() {
        return {
            form: getInitialFormData(),
            pendingGrOptions: [],
            selectedPoData: { customerName: '', poType: '', paymentTerm: '', balanceGr: 0 },
            isLoading: true,
            financeUserOptions: [],
            historyRemark: '',
        };
    },
    computed: {
        currentUser() {
            return localStorage.getItem('username') || 'System';
        },
        agingDays() {
            if (this.form.submitGrDate) {
                // Konversi tanggal submit ke zona waktu lokal (Asia/Jakarta)
                // Ini penting agar perhitungan selisih hari akurat
                const startDate = new Date(this.form.submitGrDate + 'T00:00:00');
                const endDate = new Date();
                if (isNaN(startDate)) return 'N/A';

                const diffTime = endDate - startDate;
                const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
                return `${diffDays} Hari`;
            }
            return 'N/A';
        },
        totalPoAmount() {
            return this.form.items.reduce((sum, item) => sum + (item.qty * item.unitPrice), 0);
        },
        calculatedGrAmount() {
            if (!this.totalPoAmount || !this.form.grPercentageAmount) return 0;
            return (this.form.grPercentageAmount / 100) * this.totalPoAmount;
        },
        calculatedGrAmountFormatted() {
            return this.formatCurrency(this.calculatedGrAmount);
        },
        newBalanceGrFormatted() {
            if (this.selectedPoData.balanceGr !== undefined) {
                return this.formatCurrency(this.selectedPoData.balanceGr);
            }
            return this.formatCurrency(0);
        },
    },
    methods: {
        formatCurrency(value) {
            if (value == null) return 'Rp 0';
            return `Rp ${new Intl.NumberFormat('id-ID').format(value)}`;
        },
        formatDate(value) {
            if (!value) return '-';
            const d = new Date(value);
            if (isNaN(d.getTime())) return '-';
            return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
        },
        async loadPendingGrList() {
            try {
                this.isLoading = true;
                const token = localStorage.getItem('token');
                const response = await axios.get(API_ENDPOINTS.pendingGrList, { headers: { Authorization: `Bearer ${token}` } });
                this.pendingGrOptions = response.data.map(gr => ({
                    grDetailId: gr.grDetailId,
                    label: `${gr.poNumber} — ${Number(gr.percentageGr).toFixed(2)}% (${this.formatCurrency(gr.amountGr)}) — submit ${this.formatDate(gr.submitDate)}`,
                }));
            } catch (error) {
                console.error('Error fetching pending GR list:', error);
                Swal.fire('Error', 'Gagal memuat daftar GR yang pending.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        async loadFinanceUsers() {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(API_ENDPOINTS.financeUsers, { headers: { Authorization: `Bearer ${token}` } });
                this.financeUserOptions = response.data
                    .filter(user => user.departmentName === 'Finance Actg Tax')
                    .map(user => ({ id: user.name, name: user.name }));
            } catch (error) {
                console.error('Error fetching finance users:', error);
            }
        },
        async handleGrSelection() {
            const grDetailId = this.form.grDetailId;
            if (!grDetailId) {
                this.resetForm();
                return;
            }
            this.isLoading = true;
            try {
                const token = localStorage.getItem('token');
                const grDetailResponse = await axios.get(API_ENDPOINTS.grDetailById(grDetailId), { headers: { Authorization: `Bearer ${token}` } });
                const mergedData = grDetailResponse.data;

                // ... (kode lainnya tidak berubah)
                this.selectedPoData = {
                    customerName: mergedData.customerName,
                    poType: mergedData.poType,
                    paymentTerm: mergedData.paymentTerm,
                    balanceGr: mergedData.balanceGr,
                };
                this.form.items = mergedData.products.map(p => ({
                    productId: p.productId,
                    partName: p.partName,
                    partDesc: p.partDesc,
                    qty: p.qty,
                    unitPrice: p.unitPrice,
                }));
                this.form.grId = mergedData.id;

                // --- PERUBAHAN DI SINI ---
                // Langsung potong string tanggal tanpa konversi timezone
                this.form.submitGrDate = mergedData.submitDate ? mergedData.submitDate.split('T')[0] : '';
                this.form.receivedDate = mergedData.receivedDate ? new Date(mergedData.receivedDate).toISOString().split('T')[0] : getTodayDate();
                // --- AKHIR PERUBAHAN ---

                this.form.grPercentageAmount = mergedData.percentageGr;
                this.historyRemark = mergedData.remark || 'Belum ada remark.';
                this.form.remarks = '';
                this.form.grDetailId = mergedData.grDetailId;

                if (mergedData.receivedFinanceDate && mergedData.receivedName) {
                    this.form.isFinanceReceived = true;
                    this.form.financeReceivedDate = new Date(mergedData.receivedFinanceDate).toISOString().split('T')[0];
                    this.form.financeUserId = mergedData.receivedName;
                } else {
                    this.form.isFinanceReceived = false;
                    this.form.financeReceivedDate = getTodayDate();
                    this.form.financeUserId = null;
                }

            } catch (error) {
                if (error.response && error.response.status === 404) {
                    Swal.fire('Info', `Detail GR tidak ditemukan.`, 'info');
                    this.resetForm();
                } else {
                    console.error(`Error fetching details for GR Detail ID ${grDetailId}:`, error);
                    Swal.fire('Error', 'Gagal memuat detail data.', 'error');
                }
            } finally {
                this.isLoading = false;
            }
        },
        async saveGoodsReceipt() {
            if (!this.form.grDetailId) { /* ... */ }
            if (this.form.isFinanceReceived && (!this.form.financeReceivedDate || !this.form.financeUserId)) { /* ... */ }

            let finalRemark = this.historyRemark === 'Belum ada remark.' ? '' : this.historyRemark;
            const newRemarkText = this.form.remarks.trim();

            // DIUBAH: Logika remark disamakan dengan form Entry
            if (newRemarkText) {
                const monthNames = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
                const now = new Date();
                const day = now.getDate();
                const month = monthNames[now.getMonth()];
                const year = now.getFullYear();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const timestamp = `${day} ${month} ${year} ${hours}.${minutes}`;

                let newEntry = `\n${timestamp} , user : ${this.currentUser} , ${newRemarkText}`;

                if (this.form.isFinanceReceived && this.form.financeUserId) {
                    const financeUser = this.financeUserOptions.find(u => u.id === this.form.financeUserId)?.name || this.form.financeUserId;
                    newEntry += ` (Diterima Finance oleh: ${financeUser})`;
                }
                finalRemark += newEntry;
            }

            const payload = {
                receivedDate: this.form.receivedDate || null,
                receivedFinanceDate: this.form.isFinanceReceived ? this.form.financeReceivedDate : null,
                receivedName: this.form.isFinanceReceived ? this.form.financeUserId : null,
                remark: finalRemark,
            };

            try {
                this.isLoading = true;
                const token = localStorage.getItem('token');
                await axios.put(API_ENDPOINTS.updateGoodsReceiptDetails(this.form.grDetailId), payload, { headers: { Authorization: `Bearer ${token}` } });
                Swal.fire('Berhasil', 'Data Goods Receipt berhasil diupdate.', 'success');
                this.resetForm();
                await this.loadPendingGrList();
            } catch (error) {
                console.error('Error updating Goods Receipt:', error);
                Swal.fire('Gagal', 'Terjadi kesalahan saat mengupdate data.', 'error');
            } finally {
                this.isLoading = false;
            }
        },
        async voidGoodsReceipt() {
            const confirmation = await Swal.fire({
                icon: 'warning',
                title: 'Batalkan GR ini?',
                text: 'Amount GR akan dikembalikan ke Balance GR dan tindakan ini tidak bisa diurungkan.',
                showCancelButton: true,
                confirmButtonText: 'Ya, Batalkan GR',
                confirmButtonColor: '#dc2626',
                cancelButtonText: 'Batal',
            });
            if (!confirmation.isConfirmed) return;

            try {
                this.isLoading = true;
                const token = localStorage.getItem('token');
                await axios.delete(API_ENDPOINTS.voidGoodsReceipt(this.form.grDetailId), {
                    params: { voidedBy: this.currentUser },
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire('Berhasil', 'GR berhasil dibatalkan dan balance dikembalikan.', 'success');
                this.resetForm();
                await this.loadPendingGrList();
            } catch (error) {
                console.error('Error voiding Goods Receipt:', error);
                const message = error.response && error.response.status === 400
                    ? error.response.data
                    : 'Terjadi kesalahan saat membatalkan GR.';
                Swal.fire('Gagal', message, 'error');
            } finally {
                this.isLoading = false;
            }
        },
        resetForm() {
            this.form = getInitialFormData();
            this.selectedPoData = { customerName: '', poType: '', paymentTerm: '', balanceGr: 0 };
            this.historyRemark = '';
        },
    },
    watch: {
        'form.isFinanceReceived'(isReceived) {
            if (!isReceived) {
                this.form.financeReceivedDate = getTodayDate(); // Set default ke hari ini
                this.form.financeUserId = null;
            }
        }
    },
    mounted() {
        this.loadPendingGrList();
        this.loadFinanceUsers();
    }
};
</script>

<style scoped>
.input-border {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
}

.input-border:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
    outline: none;
}

.summary-box {
    @apply bg-blue-50 border border-blue-200 rounded-lg p-4 h-full;
}

:deep(.vs__dropdown-toggle) {
    border: 1px solid #d2d6dc;
    border-radius: 0.375rem;
    padding: 4px 6px;
}

:deep(.vs--open .vs__dropdown-toggle) {
    border-color: #3b82f6;
    box-shadow: 0 0 0 1px #3b82f6;
}
</style>